import classNames from "classnames";
import { hexToRgb } from "helpers/color";
import useLabels from "hooks/useLabels";
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { useEffect } from "react";
import {
  selectMaxValue,
  updateMaxValue,
} from "store/slices/researchGapHeatmap";
import { getCDLColors } from "../../../design_system/utils/colors";

/**
 * Props for the ResearchGapHeatmapCell component
 */
type ResearchGapHeatmapCellProps = {
  /** The numerical value representing research intensity or gap size for this cell */
  number: number;
  /** Optional thread interaction ID for coordinating max values across cells in the same context */
  threadInteractionId?: string;
};

/**
 * A single cell in a research gap heatmap that visually represents the intensity
 * or magnitude of research activity in a specific area. The cell uses color
 * opacity to indicate relative intensity, with darker colors representing
 * higher values.
 *
 * Features:
 * - Color intensity based on relative value within the dataset
 * - Coordinated max value tracking via Redux for consistent scaling
 * - Displays the numerical value or placeholder text for empty cells
 * - Responsive text color based on background opacity for readability
 * - Integration with thread interactions for contextual data
 *
 */
function ResearchGapHeatmapCell({
  number,
  threadInteractionId,
}: ResearchGapHeatmapCellProps) {
  const [proAnalysisLabels] = useLabels("pro-analysis");
  const dispatch = useAppDispatch();

  // Get the maximum value for this thread interaction from Redux
  const maxValue = useAppSelector((state) =>
    threadInteractionId ? selectMaxValue(state, threadInteractionId) : 0
  );

  // Update the maximum value in Redux when number changes
  useEffect(() => {
    if (number > 0 && threadInteractionId) {
      dispatch(
        updateMaxValue({
          threadInteractionId,
          value: number,
        })
      );
    }
  }, [number, threadInteractionId, dispatch]);

  // Use the maximum value from Redux state
  const storedMaxValue = Math.max(1, maxValue);
  const opacityLevel = Math.min(1, number / storedMaxValue);

  return (
    <span
      className="research-gap-heatmap-cell inline-table h-full w-full"
      style={{
        backgroundColor: `rgba(${hexToRgb(
          getCDLColors().accentBase
        )}, ${opacityLevel})`,
      }}
    >
      <span
        className={classNames(
          "flex flex-1 h-full items-center justify-center sm-mono-semibold uppercase",
          opacityLevel > 0.5
            ? "text-accent-fg-on-accent"
            : opacityLevel > 0
              ? "text-accent-emphasis"
              : "text-fg-muted"
        )}
      >
        {number > 0
          ? number
          : proAnalysisLabels["research-gap-empty-heatmap-cell"]}
      </span>
    </span>
  );
}

/**
 * Converts research gap heatmap cell data to markdown format for plain text export
 *
 * @param props - ResearchGapHeatmapCell props containing the cell number
 * @returns Formatted markdown string with bold number or placeholder text
 */
export const ResearchGapHeatmapCellMarkdown = ({
  number,
}: ResearchGapHeatmapCellProps) => {
  const [proAnalysisLabels] = useLabels("pro-analysis");
  return `**${
    number > 0 ? number : proAnalysisLabels["research-gap-empty-heatmap-cell"]
  }**`;
};

export default ResearchGapHeatmapCell;
